import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';

const RegistrationForm = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    companyName: '',
    industry: '',
    email: '',
    password: '',
    confirmPassword: '',
    orgNumber: '',
    contactPerson: '',
    phone: '',
    website: '',
    companySize: '',
    acceptTerms: false,
    acceptPrivacy: false,
    marketingEmails: false
  });
  const [errors, setErrors] = useState({});

  const industryOptions = [
    { value: 'technology', label: 'Technology & Software' },
    { value: 'finance', label: 'Finance & Banking' },
    { value: 'healthcare', label: 'Healthcare & Medical' },
    { value: 'consulting', label: 'Consulting & Professional Services' },
    { value: 'manufacturing', label: 'Manufacturing & Industrial' },
    { value: 'retail', label: 'Retail & E-commerce' },
    { value: 'education', label: 'Education & Training' },
    { value: 'media', label: 'Media & Entertainment' },
    { value: 'energy', label: 'Energy & Utilities' },
    { value: 'construction', label: 'Construction & Real Estate' },
    { value: 'transportation', label: 'Transportation & Logistics' },
    { value: 'government', label: 'Government & Public Sector' },
    { value: 'nonprofit', label: 'Non-profit & NGO' },
    { value: 'other', label: 'Other' }
  ];

  const companySizeOptions = [
    { value: '1-10', label: '1-10 employees' },
    { value: '11-50', label: '11-50 employees' },
    { value: '51-200', label: '51-200 employees' },
    { value: '201-500', label: '201-500 employees' },
    { value: '501-1000', label: '501-1000 employees' },
    { value: '1000+', label: '1000+ employees' }
  ];

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.companyName?.trim()) {
      newErrors.companyName = 'Company name is required';
    }

    if (!formData?.industry) {
      newErrors.industry = 'Please select an industry';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (formData?.password !== formData?.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData?.contactPerson?.trim()) {
      newErrors.contactPerson = 'Contact person name is required';
    }

    if (!formData?.companySize) {
      newErrors.companySize = 'Please select company size';
    }

    if (!formData?.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and conditions';
    }

    if (!formData?.acceptPrivacy) {
      newErrors.acceptPrivacy = 'You must accept the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    // Simulate registration process
    setTimeout(() => {
      setIsLoading(false);
      navigate('/employer-dashboard');
    }, 2000);
  };

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Company Information Section */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Building2" size={20} className="mr-2 text-primary" />
            Company Information
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Company Name"
              type="text"
              placeholder="Acme Corporation"
              value={formData?.companyName}
              onChange={(e) => handleInputChange('companyName', e?.target?.value)}
              error={errors?.companyName}
              required
            />

            <Select
              label="Industry"
              placeholder="Select industry"
              options={industryOptions}
              value={formData?.industry}
              onChange={(value) => handleInputChange('industry', value)}
              error={errors?.industry}
              required
              searchable
            />

            <Input
              label="Organization Number"
              type="text"
              placeholder="123 456 789 (Optional)"
              description="Norwegian business registration number"
              value={formData?.orgNumber}
              onChange={(e) => handleInputChange('orgNumber', e?.target?.value)}
            />

            <Select
              label="Company Size"
              placeholder="Select company size"
              options={companySizeOptions}
              value={formData?.companySize}
              onChange={(value) => handleInputChange('companySize', value)}
              error={errors?.companySize}
              required
            />

            <Input
              label="Website"
              type="url"
              placeholder="https://company.com (Optional)"
              value={formData?.website}
              onChange={(e) => handleInputChange('website', e?.target?.value)}
            />
          </div>
        </div>

        {/* Contact Information Section */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="User" size={20} className="mr-2 text-primary" />
            Contact Information
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Contact Person"
              type="text"
              placeholder="John Doe"
              value={formData?.contactPerson}
              onChange={(e) => handleInputChange('contactPerson', e?.target?.value)}
              error={errors?.contactPerson}
              required
            />

            <Input
              label="Phone Number"
              type="tel"
              placeholder="+47 123 45 678 (Optional)"
              value={formData?.phone}
              onChange={(e) => handleInputChange('phone', e?.target?.value)}
            />

            <Input
              label="Email Address"
              type="email"
              placeholder="john@company.com"
              value={formData?.email}
              onChange={(e) => handleInputChange('email', e?.target?.value)}
              error={errors?.email}
              required
            />
          </div>
        </div>

        {/* Account Security Section */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="Lock" size={20} className="mr-2 text-primary" />
            Account Security
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4">
            <Input
              label="Password"
              type="password"
              placeholder="Create a strong password"
              description="Minimum 8 characters"
              value={formData?.password}
              onChange={(e) => handleInputChange('password', e?.target?.value)}
              error={errors?.password}
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              placeholder="Confirm your password"
              value={formData?.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e?.target?.value)}
              error={errors?.confirmPassword}
              required
            />
          </div>
        </div>

        {/* Terms and Privacy Section */}
        <div className="bg-card rounded-xl p-6 border border-border">
          <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
            <Icon name="FileText" size={20} className="mr-2 text-primary" />
            Legal Agreement
          </h3>
          
          <div className="space-y-4">
            <Checkbox
              label="I accept the Terms and Conditions"
              description="By checking this, you agree to our terms of service"
              checked={formData?.acceptTerms}
              onChange={(e) => handleInputChange('acceptTerms', e?.target?.checked)}
              error={errors?.acceptTerms}
              required
            />

            <Checkbox
              label="I accept the Privacy Policy"
              description="We are GDPR compliant and protect your data"
              checked={formData?.acceptPrivacy}
              onChange={(e) => handleInputChange('acceptPrivacy', e?.target?.checked)}
              error={errors?.acceptPrivacy}
              required
            />

            <Checkbox
              label="Send me marketing emails and product updates"
              description="You can unsubscribe at any time"
              checked={formData?.marketingEmails}
              onChange={(e) => handleInputChange('marketingEmails', e?.target?.checked)}
            />
          </div>
        </div>

        {/* Submit Button */}
        <div className="flex items-center justify-between pt-4">
          <Button 
            variant="ghost" 
            type="button"
            onClick={() => navigate('/role-selection-modal')}
          >
            Back to Role Selection
          </Button>
          
          <Button
            type="submit"
            loading={isLoading}
            iconName="ArrowRight"
            iconPosition="right"
            disabled={isLoading}
          >
            {isLoading ? 'Creating Account...' : 'Create Employer Account'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default RegistrationForm;